import { BillingEntityTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import type { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import type { Client } from '@api/READ_ONLY/client_api/Api';
import type { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import ExternalProviderSelect from '@app/components/ui/ExternalProviderSelect/ExternalProviderSelect';
import type { ExternalProvider } from '@api/READ_ONLY/caregiver_api/Api';
import CaregiversSelectGeneric from '@feat-caregivers/components/CaregiversSelect';
import ClientSelectGeneric from '@feat-clients/components/ClientSelect';
import DoctorSelectGeneric from '@feat-doctors/components/DoctorSelect/DoctorSelectGeneric';
import { DatePicker, Select } from 'antd';
import type { PickerLocale } from 'antd/es/date-picker/generatePicker';
import type { Dayjs } from 'dayjs';
import type { EntityType } from './useCustomerProfile';

type SelectOption<Value> = {
  value: Value;
  label: string;
};

type CustomerProfileFiltersProps = {
  entityTypeValue: EntityType;
  entityTypeOptions: Array<SelectOption<EntityType>>;
  entityIdentifierValue?: number;
  startingDateValue: Dayjs | null;
  endingDateValue: Dayjs | null;
  dateFormat: string;
  datePickerLocale: PickerLocale;
  onEntityTypeChange: (value: EntityType) => void;
  onEntityIdentifierChange: (value: number | undefined) => void;
  onStartingDateChange: (value: Dayjs | null) => void;
  onEndingDateChange: (value: Dayjs | null) => void;
  selectPlaceholder: string;
  startingDatePlaceholder: string;
  endingDatePlaceholder: string;
  isEntitySelectorDisabled?: boolean;
  selectedClient?: Client;
  selectedDoctor?: Doctor;
  selectedCaregiver?: CaregiverMinified;
  selectedProvider?: ExternalProvider;
};

const CustomerProfileFilters = ({
  entityTypeValue,
  entityTypeOptions,
  entityIdentifierValue,
  startingDateValue,
  endingDateValue,
  dateFormat,
  datePickerLocale,
  onEntityTypeChange,
  onEntityIdentifierChange,
  onStartingDateChange,
  onEndingDateChange,
  selectPlaceholder,
  startingDatePlaceholder,
  endingDatePlaceholder,
  isEntitySelectorDisabled = false,
  selectedClient,
  selectedDoctor,
  selectedCaregiver,
  selectedProvider,
}: CustomerProfileFiltersProps) => {
  const renderEntityIdentifierSelector = () => {
    if (entityTypeValue === 'client') {
      return (
        <ClientSelectGeneric
          size="large"
          value={entityIdentifierValue}
          allowClear
          placeholder={selectPlaceholder}
          disabled={isEntitySelectorDisabled}
          selectedItemsProvider={() => selectedClient}
          onClientChange={(selectedClient) => {
            if (selectedClient && !Array.isArray(selectedClient)) {
              onEntityIdentifierChange(selectedClient.clientId);
              return;
            }

            onEntityIdentifierChange(undefined);
          }}
        />
      );
    }

    if (entityTypeValue === 'doctor') {
      return (
        <DoctorSelectGeneric
          size="large"
          value={entityIdentifierValue}
          allowClear
          placeholder={selectPlaceholder}
          disabled={isEntitySelectorDisabled}
          lazyLoadOnOpen
          selectedItemsProvider={() => selectedDoctor}
          onDoctorChange={(selectedDoctor) => {
            if (selectedDoctor && !Array.isArray(selectedDoctor)) {
              onEntityIdentifierChange(selectedDoctor.doctorId);
              return;
            }

            onEntityIdentifierChange(undefined);
          }}
        />
      );
    }

    // A caregiver_cash_register account is keyed by the caregiver id, so the picker
    // reuses the caregiver search exactly like the plain `caregiver` type.
    if (entityTypeValue === 'caregiver' || entityTypeValue === BillingEntityTypeEnum.CaregiverCashRegister) {
      return (
        <CaregiversSelectGeneric
          size="large"
          value={entityIdentifierValue}
          allowClear
          placeholder={selectPlaceholder}
          disabled={isEntitySelectorDisabled}
          lazyLoadOnOpen
          selectedItemsProvider={() => selectedCaregiver}
          onCaregiverChange={(selectedCaregiver) => {
            if (selectedCaregiver && !Array.isArray(selectedCaregiver)) {
              onEntityIdentifierChange(selectedCaregiver.caregiverId);
              return;
            }

            onEntityIdentifierChange(undefined);
          }}
        />
      );
    }

    if (entityTypeValue === BillingEntityTypeEnum.Provider) {
      // Includes deactivated providers: their ledger stays readable.
      return (
        <ExternalProviderSelect
          size="large"
          className="w-full"
          value={entityIdentifierValue}
          allowClear
          placeholder={selectPlaceholder}
          disabled={isEntitySelectorDisabled}
          includeInactive
          selectedProvider={selectedProvider}
          onProviderChange={(provider) => onEntityIdentifierChange(provider?.externalProviderId)}
        />
      );
    }

    return <Select size="large" className="w-full" value={undefined} disabled placeholder={selectPlaceholder} />;
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="grid w-full items-center gap-2 md:grid-cols-[210px_250px_160px_160px]">
        <Select
          size="large"
          className="w-full"
          value={entityTypeValue}
          onChange={onEntityTypeChange}
          options={entityTypeOptions}
        />

        {renderEntityIdentifierSelector()}

        <DatePicker
          size="large"
          className="w-full"
          format={dateFormat}
          locale={datePickerLocale}
          value={startingDateValue}
          onChange={onStartingDateChange}
          placeholder={startingDatePlaceholder}
        />

        <DatePicker
          size="large"
          className="w-full"
          format={dateFormat}
          locale={datePickerLocale}
          value={endingDateValue}
          onChange={onEndingDateChange}
          placeholder={endingDatePlaceholder}
        />
      </div>
    </div>
  );
};

export default CustomerProfileFilters;
